<template>
    <div class="chat-private-side">
        <div class="friend-info-section" v-if="hasUserInfo">
            <div class="friend-side-content">
                <!-- 好友基本信息卡片 -->
                <div class="friend-card">
                    <profile-summary
                        :avatar-name="profileTitle"
                        :avatar-url="userInfo.headImageThumb"
                        :avatar-size="52"
                        :title="profileTitle"
                    >
                        <template #avatar>
                            <head-image :size="52" :url="userInfo.headImageThumb" radius="50%" :name="profileTitle" :online="Boolean(userInfo.online)">
                            </head-image>
                        </template>
                        <template #title-extra>
                            <div class="gender-icons">
                                <i v-if="userInfo.sex == 0" class="el-icon-male gender-icon male"></i>
                                <i v-if="userInfo.sex == 1" class="el-icon-female gender-icon female"></i>
                            </div>
                        </template>
                        <template #extra>
                            <div class="friend-id-row">
                                <span class="friend-id">ID: {{ userInfo.userName }}</span>
                                <button class="copy-btn" type="button" @click="copyUserName" title="复制用户ID">
                                    <i class="el-icon-copy-document"></i>
                                </button>
                            </div>
                        </template>
                    </profile-summary>
                    <div class="friend-meta-row" :class="{ 'is-empty': !isDnd && !isInBlacklist }">
                        <span v-show="isDnd" class="meta-chip">
                            <i class="el-icon-bell"></i>
                            免打扰
                        </span>
                        <span v-show="isInBlacklist" class="meta-chip danger">
                            <i class="el-icon-user"></i>
                            黑名单
                        </span>
                    </div>
                </div>
                <!-- 好友信息区域 -->
                <div class="friend-info-section-card">
                    <!-- 备注名 -->
                    <div class="info-item">
                        <div class="info-label">
                            <i class="el-icon-edit"></i>
                            <span>备注</span>
                        </div>
                        <div class="info-content">
                            <el-input v-if="isEditingRemark" v-model="remarkValue" size="small" placeholder="请输入备注名"
								maxlength="32" :disabled="isInteractionLocked" @blur="saveRemark" @keyup.enter.native="saveRemark"
                                ref="remarkInput">
                            </el-input>
							<div v-else class="info-display" :class="{ 'info-display--readonly': Boolean(pendingAction) }" @click="startEditRemark">
                                <span v-if="friend.remarkNickName" :title="friend.remarkNickName">{{ friend.remarkNickName }}</span>
                                <span v-else class="info-placeholder">未设置</span>
                                <i class="el-icon-edit-outline"></i>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 个人设置区域 -->
                <div class="personal-setting">
                    <div class="switch-item">
                        <div class="label">
                            <i class="el-icon-bell"></i>
                            <span>免打扰</span>
                        </div>
						<chat-setting-switch v-model="isDnd" :pending="pendingSettingKey === 'dnd'"
							:blocked="Boolean(pendingSettingKey)" :disabled="settingsDisabled"
							@change="onDndChange"></chat-setting-switch>
                    </div>
                    <div class="switch-item">
                        <div class="label">
                            <i class="el-icon-top"></i>
                            <span>置顶</span>
                        </div>
						<chat-setting-switch v-model="isTop" :pending="pendingSettingKey === 'top'"
							:blocked="Boolean(pendingSettingKey)" :disabled="settingsDisabled"
							@change="onTopChange"></chat-setting-switch>
                    </div>
                    <div class="switch-item">
                        <div class="label">
                            <i class="el-icon-warning"></i>
                            <span>黑名单</span>
                        </div>
						<chat-setting-switch v-model="isInBlacklist" :pending="pendingSettingKey === 'blacklist'"
							:blocked="Boolean(pendingSettingKey)" :disabled="settingsDisabled"
							@change="onBlacklistChange"></chat-setting-switch>
                    </div>
                </div>

                <!-- 操作按钮区域 -->
                <div class="btn-group">
                    <div class="action-row">
						<div class="text-btn" :class="{ 'is-disabled': Boolean(pendingAction) }" @click="onCleanMessage">
                            <i class="el-icon-delete"></i>
                            清空记录
                        </div>
						<div class="text-btn text-btn--danger" :class="{ 'is-disabled': Boolean(pendingAction) }" @click="onDeleteFriend">
                            <i class="el-icon-close"></i>
                            删除好友
                        </div>
                    </div>
                </div>
				<div class="complaint-tip" :class="{ 'is-disabled': Boolean(pendingAction) }" @click="onComplaint">
                    <i class="el-icon-warning-outline"></i>
                    <span>投诉此人</span>
                </div>
            </div>
        </div>
        <empty-state
            v-else
            class="side-empty-state"
            icon="el-icon-user-solid"
            title="选择联系人查看资料"
            hint="免打扰、备注和好友操作会在这里展示"
        ></empty-state>
        <complaint ref="complaint"></complaint>
    </div>
</template>

<script>
import HeadImage from '../common/HeadImage.vue';
import Complaint from '../common/Complaint.vue';
import ProfileSummary from '../common/ProfileSummary.vue';
import ChatSettingSwitch from './ChatSettingSwitch.vue';

export default {
    name: "ChatPrivateSide",
    components: {
		HeadImage,
		Complaint,
		ProfileSummary,
		ChatSettingSwitch
    },
    data() {
        return {
            isDnd: false,
            isTop: false,
            isInBlacklist: false,
            isEditingRemark: false,
			remarkValue: '',
			pendingAction: '',
			pendingSettingKey: ''
        }
    },
    props: {
        userInfo: {
            type: Object,
            default: null
        },
        chat: {
            type: Object,
            default: null
        }
    },
    methods: {
        conversationKey() {
            return this.hasUserInfo ? { type: 'PRIVATE', targetId: this.userInfo.id } : null;
        },
        syncConversationSettings() {
            const key = this.conversationKey();
            this.isDnd = key ? this.chatStore.isDnd(key) : false;
            this.isTop = key ? this.chatStore.isTop(key) : false;
        },
		async runPendingAction(actionKey, handler) {
			if (this.isInteractionLocked) {
                return false;
            }
            this.pendingAction = actionKey;
            try {
                await handler();
                return true;
            } finally {
                if (this.pendingAction === actionKey) {
                    this.pendingAction = '';
                }
            }
        },
		async runSettingAction(settingKey, handler) {
			if (this.settingsDisabled || this.pendingSettingKey) {
				return false;
			}
			this.pendingSettingKey = settingKey;
			try {
				await handler();
				return true;
			} finally {
				if (this.pendingSettingKey === settingKey) {
					this.pendingSettingKey = '';
				}
			}
		},
		onDndChange(value) {
			if (!this.hasUserInfo || this.isInteractionLocked || this.isEditingRemark) {
                return;
            }
            const data = {
                friendId: this.userInfo.id,
                isDnd: value
            };
            this.runSettingAction('dnd', async () => {
                await this.$http({
                    url: '/friend/dnd',
                    method: 'PUT',
                    data
                });
                this.chatStore.setDnd(this.conversationKey(), value);
                this.$message.success(value ? '已开启免打扰' : '已关闭免打扰');
            }).catch(() => {
                this.$message.error('操作失败，请重试');
                this.isDnd = !value;
            });
        },
		onTopChange(value) {
			if (!this.hasUserInfo || this.isInteractionLocked || this.isEditingRemark) {
                return;
            }
            const data = {
                friendId: this.userInfo.id,
                isTop: value
            };
            this.runSettingAction('top', async () => {
                await this.$http({
                    url: '/friend/top',
                    method: 'PUT',
                    data
                });
                this.chatStore.setTop(this.conversationKey(), value);
                this.$message.success(value ? '已置顶聊天' : '已取消置顶');
            }).catch(() => {
                this.$message.error('操作失败，请重试');
                this.isTop = !value;
            });
        },
		onBlacklistChange(value) {
			if (!this.hasUserInfo || this.isInteractionLocked || this.isEditingRemark) {
                return;
            }
            if (value) {
                this.$confirm(`您将不再收到对方的消息,确认将 '${this.userInfo.nickName}'加入黑名单吗?`, '加入黑名单', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                }).then(() => {
                    this.updateBlacklistSetting(true);
                }).catch(() => {
                    this.isInBlacklist = false;
                });
                return;
            }
            this.updateBlacklistSetting(false);
        },
        updateBlacklistSetting(value) {
            const requestConfig = value
                ? { url: `/blacklist/add?userId=${this.userInfo.id}`, method: 'POST' }
                : { url: `/blacklist/remove?userId=${this.userInfo.id}`, method: 'DELETE' };
            this.runSettingAction('blacklist', async () => {
                await this.$http(requestConfig);
                this.isInBlacklist = value;
                this.userInfo.isInBlacklist = value;
                const tip = value ? `已将'${this.userInfo.nickName}'加入黑名单` : `已解除'${this.userInfo.nickName}'的黑名单`;
                this.$message.success(tip);
            }).catch(() => {
                this.$message.error('操作失败，请重试');
                this.isInBlacklist = !value;
            });
        },
        onCleanMessage() {
			if (!this.hasUserInfo || this.isInteractionLocked) {
                return;
            }
            this.$confirm(`确定要清空与'${this.userInfo.nickName}'的所有聊天记录吗？`, '清空聊天记录', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                this.chatStore.cleanMessage(this.chat);
                this.$message.success(`您清空了'${this.userInfo.nickName}'的聊天记录`);
            });
        },
        startEditRemark() {
			if (!this.hasUserInfo || this.isInteractionLocked) {
                return;
            }
            this.isEditingRemark = true;
            this.remarkValue = this.friend.remarkNickName || '';
            this.$nextTick(() => {
                this.$refs.remarkInput.focus();
            });
        },
        saveRemark() {
            if (!this.hasUserInfo) {
                return;
            }
            const newRemark = this.remarkValue.trim();
            if (newRemark === (this.friend.remarkNickName || '')) {
                this.isEditingRemark = false;
                return;
            }
            const data = {
                friendId: this.userInfo.id,
                remarkNickName: newRemark
            };
			this.runPendingAction('remark', async () => {
				const friend = await this.$http({
					url: '/friend/update/remark',
					method: 'PUT',
					data
				});
				this.friendStore.updateFriend(friend);
				this.groupStore.syncGroupMemberFromFriend(friend);
				this.chatStore.updateChatFromFriend(friend);
				this.$message.success(newRemark ? '备注设置成功' : '备注已清除');
			}).catch(() => {
                this.$message.error('备注保存失败，请重试');
            }).finally(() => {
                this.isEditingRemark = false;
            });
        },
        onDeleteFriend() {
			if (!this.hasUserInfo || this.isInteractionLocked) {
                return;
            }
            this.$confirm(`确定要删除好友 '${this.userInfo.nickName}' 吗？删除后将无法恢复。`, '删除好友', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                this.runPendingAction('delete', async () => {
                    await this.$http({
                        url: `/friend/delete/${this.userInfo.id}`,
                        method: 'DELETE'
                    });
                    this.friendStore.removeFriend(this.userInfo.id);
                    this.friendGroupStore.removeFriend(this.userInfo.id);
                    this.chatStore.removePrivateChat(this.userInfo.id);
                    this.$message.success(`已删除好友 '${this.userInfo.nickName}'`);
                    this.$emit('close');
                }).catch(() => {
                    this.$message.error('删除失败，请重试');
                });
            });
        },
        onComplaint() {
			if (!this.hasUserInfo || this.isInteractionLocked) {
                return;
            }
            this.$refs.complaint.open(1, this.userInfo.id, this.userInfo.nickName)
        },
        copyUserName() {
            if (!this.hasUserInfo) {
                return;
            }
            const userName = this.userInfo.userName;
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(userName).then(() => {
                    this.$message.success(`内容'${userName}'已复制到剪贴板`);
                }).catch(() => {
                    this.$message.error('复制失败，请手动复制');
                });
            } else {
                this.$message.error('复制失败，请手动复制');
            }
        }
    },
    computed: {
        hasUserInfo() {
            return Boolean(this.userInfo && this.userInfo.id);
        },
		isInteractionLocked() {
			return Boolean(this.pendingAction || this.pendingSettingKey);
		},
		settingsDisabled() {
			return Boolean(this.pendingAction || this.isEditingRemark);
		},
        conversationStateVersion() {
            return this.chatStore.conversationStateVersion;
        },
        friend() {
            return this.hasUserInfo ? (this.friendStore.findFriend(this.userInfo.id) || {}) : {};
        },
        profileTitle() {
            return this.userInfo.nickName || this.userInfo.userName || '未设置昵称';
        }
    },
    watch: {
        userInfo: {
            handler() {
                if (!this.hasUserInfo) {
                    this.isDnd = false;
                    this.isTop = false;
                    this.isInBlacklist = false;
                    return;
                }
                this.syncConversationSettings();
                this.isInBlacklist = this.userInfo.isInBlacklist;
            },
            immediate: true
        },
        conversationStateVersion: {
            handler() {
                this.syncConversationSettings();
            },
            immediate: true
        }
    }
}
</script>

<style scoped lang="scss">
.chat-private-side {
    --side-card-gap: 8px;
    --side-field-height: 36px;
    --side-field-radius: 9px;
    padding: 10px 12px 12px;
    position: relative;
    height: 100%;
    overflow-y: auto;
    scrollbar-gutter: stable;
    box-sizing: border-box;
    background: var(--im-chat-bg);

    // 公共卡片样式
    %card-style {
        padding: 10px 12px;
        background: transparent;
        border-radius: 9px;
        border: 1px solid var(--im-surface-panel-border);
        box-shadow: none;
    }

    .side-empty-state {
        height: 100%;
    }

    .friend-info-section {
        .friend-side-content {
            display: flex;
            flex-direction: column;
            gap: var(--side-card-gap);
        }

        .friend-card {
            display: flex;
            flex-direction: column;
            gap: 8px;
            @extend %card-style;
            .gender-icons {
                .gender-icon {
                    font-size: 12px;
                    border-radius: 50%;
                    padding: 2px;

                    &.male {
                        color: var(--im-color-primary);
                        background: rgba(var(--im-primary-rgb), 0.1);
                    }

                    &.female {
                        color: var(--im-color-danger);
                        background: rgba(var(--im-color-danger), 0.1);
                    }
                }
            }

            .friend-id-row {
                display: flex;
                align-items: center;
                gap: 6px;
                min-height: 18px;

                .friend-id {
                    margin: 0;
                    font-size: 13px;
                    color: var(--im-text-color-light);
                    text-align: left;
                }

                .copy-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 14px;
                    color: var(--im-color-primary);
                    cursor: pointer;
                    width: 24px;
                    height: 24px;
                    padding: 0;
                    border: none;
                    background: transparent;
                    border-radius: 6px;
                    transition: background 0.2s ease, opacity 0.2s ease;
                    opacity: 0.8;

                    &:hover {
                        background: rgba(var(--im-primary-rgb), 0.1);
                        opacity: 1;
                    }
                }
            }

            .friend-meta-row {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 6px;
                min-height: 20px;
                margin-top: 1px;

                &.is-empty {
                    visibility: hidden;
                }
            }

            .meta-chip {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                height: 20px;
                padding: 0 7px;
                border-radius: 999px;
                background: rgba(var(--im-black-rgb), 0.04);
                color: var(--im-text-color-light);
                font-size: 11px;
                font-weight: 600;

                i {
                    font-size: 13px;
                }

                &.danger {
                    background: rgba(var(--im-danger-rgb), 0.12);
                    color: var(--im-color-danger);
                }
            }
        }

        .personal-setting {
            padding: 4px 6px;
            @extend %card-style;

            .switch-item {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 12px;
                padding: 7px 8px;
                min-height: 38px;
                border-bottom: 1px solid var(--im-surface-panel-border);
                transition: background 0.2s ease;
                border-radius: 7px;

                &:last-child {
                    border-bottom: none;
                }

                &:hover {
                    background: rgba(var(--im-primary-rgb), 0.04);
                }

                .label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 13px;
                    font-weight: 500;
                    color: var(--im-text-color-primary);

                    i {
                        color: var(--im-color-primary);
                        font-size: 14px;
                        width: 16px;
                        text-align: center;
                    }
                }

			}
		}

        .friend-info-section-card {
            @extend %card-style;

            .info-item {
                padding-top: 0;

                .info-label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin: 0 0 7px;
                    font-weight: 500;
                    color: var(--im-text-color-primary);
                    font-size: 13px;

                    i {
                        color: var(--im-color-primary);
                        font-size: 14px;
                        width: 16px;
                        text-align: center;
                    }
                }

                .info-content {
                    display: flex;
                    align-items: center;
                    min-height: var(--side-field-height);
                    width: 100%;

                    ::v-deep .el-input {
                        width: 100%;
                        display: block;
                    }

                    ::v-deep .el-input__inner {
                        height: var(--side-field-height);
                        line-height: var(--side-field-height);
                        padding: 0 10px;
                        border-radius: var(--side-field-radius);
                        border-color: var(--im-control-border);
                        font-size: 14px;
                        box-sizing: border-box;
                    }

                    ::v-deep .el-input__inner:focus {
                        border-color: rgba(var(--im-primary-rgb), 0.38);
                        box-shadow: none;
                    }

                    .info-display {
                        display: flex;
                        align-items: center;
                        justify-content: flex-start;
                        padding: 0 9px;
                        cursor: pointer;
                        transition: background 0.2s ease;
                        min-height: var(--side-field-height);
                        width: 100%;
                        border-radius: var(--side-field-radius);
                        background: var(--im-control-soft-bg);
                        box-sizing: border-box;

                        &:hover {
                            background: var(--im-control-soft-bg-hover);
                        }

                        &.info-display--readonly {
                            cursor: not-allowed;
                            opacity: 0.7;
                        }

                        span {
                            flex: 1;
                            display: block;
                            font-size: 14px;
                            line-height: 1.45;
                            color: var(--im-text-color);
                            min-width: 0;
                            overflow: hidden;
                            white-space: nowrap;
                            text-overflow: ellipsis;

                            &.info-placeholder {
                                display: inline-flex;
                                align-items: center;
                                color: var(--im-text-color-light);
                                font-style: normal;
                            }
                        }

                        i {
                            color: var(--im-text-color-light);
                            font-size: 12px;
                            opacity: 0.6;
                            margin-left: auto;
                        }
                    }
                }
            }
        }

        .btn-group {
            text-align: left;
            @extend %card-style;

            .action-row {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
            }

            .text-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 4px;
                flex: 1 1 calc(50% - 3px);
                min-width: 0;
                height: 34px;
                padding: 0 10px;
                font-size: 13px;
                font-weight: 500;
                cursor: pointer;
                transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
                border-radius: 7px;
                color: var(--im-text-color);
                background: var(--im-control-soft-bg);
                border: 1px solid var(--im-control-border);
                box-sizing: border-box;

                &:hover {
                    color: var(--im-color-primary);
                    background: rgba(var(--im-primary-rgb), 0.08);
                    border-color: rgba(var(--im-primary-rgb), 0.16);
                }

                &.is-disabled {
                    cursor: not-allowed;
                    opacity: 0.6;
                    pointer-events: none;
                }

                i {
                    font-size: 13px;
                }

                &.text-btn--danger {
                    color: var(--im-color-danger);
                    background: rgba(var(--im-danger-rgb), 0.06);
                    border-color: rgba(var(--im-danger-rgb), 0.12);

                    &:hover {
                        color: var(--im-color-danger);
                        background: rgba(var(--im-danger-rgb), 0.11);
                        border-color: rgba(var(--im-danger-rgb), 0.2);
                    }
                }
            }
        }

        .complaint-tip {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            min-height: 34px;
            padding: 0 12px;
            font-size: 13px;
            color: var(--im-text-color-light);
            cursor: pointer;
            background: var(--im-control-soft-bg);
            border: 1px solid var(--im-control-border);
            border-radius: 9px;
            transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease;

            &:hover {
                color: var(--im-color-primary);
                background: rgba(var(--im-primary-rgb), 0.08);
                border-color: rgba(var(--im-primary-rgb), 0.14);
            }

            &.is-disabled {
                cursor: not-allowed;
                opacity: 0.6;
                pointer-events: none;
            }

            i {
                font-size: 13px;
            }
        }
    }
}

@media (max-width: 640px) {
    .chat-private-side {
        padding: 10px;

        .friend-info-section .friend-side-content {
            gap: var(--side-card-gap);
        }

        .friend-info-section .btn-group .text-btn {
            flex-basis: 100%;
        }
    }
}
</style>
